import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, of, catchError } from 'rxjs';
import { User } from '../models/user.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private apiUrl = `${environment.apiUrl}/users`;
  private http = inject(HttpClient);

  // Cache the user data
  private currentUser: User | null = null;


  /**
   * Get the current user's profile
   */
  getCurrentUser(): Observable<User> {
    console.log('[UserService] Fetching current user profile from API');
    return this.http.get<User>(`${this.apiUrl}/profile`).pipe(
      map(user => {
        console.log('[UserService] Raw API response for user profile:', user);
        console.log('[UserService] Profile complete flag in API response:', user?.profile_complete);

        const processedUser = this.processProfileImage(user);
        console.log('[UserService] Processed user data:', processedUser);

        this.currentUser = processedUser;
        return processedUser;
      }),
      catchError(error => {
        console.warn('[UserService] Error fetching user profile:', error);
        return of(null as unknown as User);
      })
    );
  }

  /**
   * Update the current user's profile
   */
  updateProfile(userData: Partial<User>): Observable<User> {
    // Make a copy of the user data to avoid modifying the original
    const userToSend = { ...userData };

    // If the profile_image is a data URL, extract the base64 part
    if (userToSend.profile_image && userToSend.profile_image.startsWith('data:')) {
      const parts = userToSend.profile_image.split(',');
      if (parts.length === 2) {
        userToSend.profile_image = parts[1];
      }
    }

    return this.http.put<User>(`${this.apiUrl}/profile`, userToSend).pipe(
      map(user => {
        const processedUser = this.processProfileImage(user);
        this.currentUser = processedUser;
        return processedUser;
      }),
      catchError(error => {
        console.error('Error updating user profile:', error);
        return of(null as unknown as User);
      })
    );
  }



  /**
   * Process profile image URLs to ensure they are properly formatted
   */
  private processProfileImage(user: User): User {
    const ensureUrl = (value?: string): string | undefined => {
      if (!value) return undefined;
      if (value.startsWith('data:') || value.startsWith('http://') || value.startsWith('https://')) {
        return value;
      }
      const apiBaseUrl = environment.apiUrl; // Get base URL
      if (value.startsWith('/')) {
        return apiBaseUrl + value;
      }
      return `${apiBaseUrl}/storage/${value}`;
    };

    if (user) {
      if (user.profile_image) {
        user.profile_image = ensureUrl(user.profile_image) as string;
      }
      if (user.avatar) {
        user.avatar = ensureUrl(user.avatar) as string;
      }
    }
    return user;
  }
}
